import { ClubBookingApiClient } from "../../common/api/ClubBookingApiClient";
import {
  BdOrganisation,
  BdOrganisations,
  SaveBdOrganisationRequest,
} from "../../model/members/BdOrganisation";

/**
 * The Business Development CRM's organisations (FORT-362).
 *
 * Note there is no per-record access scoping here, unlike the Membership CRM: every logged-in
 * user sees every organisation and may add one. The screen's Mine filter is a view, applied
 * in the browser.
 */
export class BdOrganisationService extends ClubBookingApiClient {
  /** Every organisation in the tenant, due follow-ups first, with the club's KPI counts. */
  async getOrganisations(): Promise<BdOrganisations> {
    // A failed GET resolves to undefined rather than rejecting, so without this a 500 would
    // render as "no organisations" — an empty CRM and a broken one look identical.
    const raw = await this.get<BdOrganisations>("/bd/organisations");
    if (raw === undefined) throw new Error("Failed to load organisations.");
    return raw;
  }

  /**
   * My organisations whose follow-up is due today or overdue — Staff Daily's block. The
   * staff member is resolved server-side from the caller, so none is named here.
   */
  async getMyDueOrganisations(): Promise<BdOrganisation[]> {
    const raw = await this.get<BdOrganisation[]>(
      "/bd/organisations",
      { dueOnly: "true" },
      { showLoading: false }
    );
    return Array.isArray(raw) ? raw : [];
  }

  /** Creates when `id` is null, updates otherwise. The caller owns the messaging. */
  async saveOrganisation(request: SaveBdOrganisationRequest): Promise<BdOrganisation | undefined> {
    if (request.id == null) {
      return this.post<BdOrganisation>("/bd/organisations", request, { showSuccess: false, showError: false });
    }
    return this.put<BdOrganisation>(`/bd/organisations/${request.id}`, request, { showSuccess: false, showError: false });
  }

  /**
   * Signs a follow-up off as done (FORT-367) — records who and when, and clears the
   * follow-up date so it leaves the due list. The acting staff member is resolved
   * server-side, and the write only matches an organisation that is mine (or unowned),
   * so a 404 here means "not yours", not "gone". The caller owns the messaging.
   */
  async signOffOrganisation(id: number): Promise<BdOrganisation | undefined> {
    return this.post<BdOrganisation>(`/bd/organisations/${id}/signoff`, {}, {
      showSuccess: false,
      showError: false,
    });
  }
}
